/**
 * useFeedbackStatus — om familien kan sende tilbakemelding i det hele tatt
 * (#187).
 *
 * `GET /api/feedback/ai-status` svarer `enabled: false` når familiens
 * `feedback_enabled` er av eller GitLab ikke er satt opp på instansen. Da
 * skjules både knappen i toppen og raden i Meg, i stedet for at innsendingen
 * ender i den generelle feilmeldingen.
 *
 * Knappen står i toppen på nesten hver skjerm, så svaret caches i fem
 * minutter og deles av alle; arket spør på nytt om AI-utkastet når det åpnes
 * (`checkAiAvailability`), siden budsjettet kan endre seg underveis.
 */
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../auth/useSession';

export const FEEDBACK_STATUS_KEY = ['feedback', 'status'] as const;

interface FeedbackStatus {
  enabled: boolean;
  available: boolean;
}

export function useFeedbackStatus(): boolean {
  const { data: session } = useSession();
  const memberId = session?.member?.id;
  const { data } = useQuery({
    queryKey: [...FEEDBACK_STATUS_KEY, memberId],
    enabled: !!memberId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<FeedbackStatus> => {
      const res = await fetch('/api/feedback/ai-status', { credentials: 'same-origin' });
      if (!res.ok) return { enabled: false, available: false };
      const body = (await res.json()) as Partial<FeedbackStatus>;
      return { enabled: body.enabled === true, available: body.available === true };
    },
  });
  return data?.enabled === true;
}
